// @flow strict
import * as React from 'react';

import type { State, Grade } from './state-list.js';
import Select from './components/select.jsx';
import { getGrade, getPartner } from './skills-utils.js';
import styles from './ald-selection.css';

type PartnerSelectProps = {
  partners:StateItem[],
  selectedPartnerId:?string,
  changeSelection:(?string) => void
}
const PartnerSelect = ({ partners, selectedPartnerId, changeSelection }:PartnerSelectProps) => {
  return (
    <Select
      changeSelection={changeSelection}
      itemDisplayProp="stateDisplay"
      itemKeyProp="stateId"
      items={partners}
      itemValueProp="stateId"
      label="Partner"
      noSelectionValue="Select partner"
      selectedItemValue={selectedPartnerId}
      dataCyValue="partner-select"
    />
  );
};//:{partners:?Array<string>, selectedPartnerId:?string, changeSelection:?string}

type GradeSelectProps = {grades:GradeItem[], selectedGradeId:?string, changeSelection:(?string) => void}

const GradeSelect = ({ grades, selectedGradeId, changeSelection }:GradeSelectProps) => {
  grades.sort((a,b)=>{
    //Our gradeId values are not numeric and don't always contain a number at all.
    //Fall back on length and then alpha sort
    if(a.gradeId.length===b.gradeId.length){
      return a.gradeId.localeCompare(b.gradeId)
    }
    return a.gradeId.length-b.gradeId.length
  })
  return (
    <Select
      changeSelection={changeSelection}
      itemDisplayProp="gradeDisplay"
      itemKeyProp="gradeId"
      items={grades}
      itemValueProp="gradeId"
      label="Grade"
      noSelectionValue="Select grade"
      selectedItemValue={selectedGradeId}
      dataCyValue="grade-select"
    />
  );
};

type SubjectSelectProps = {subjects:SubjectItem[], selectedSubjectId:?string, changeSelection:(?string) => void}

const SubjectSelect = ({ subjects, selectedSubjectId, changeSelection }:SubjectSelectProps) => {
  return (
    <Select
      changeSelection={changeSelection}
      itemDisplayProp="subjectDisplay"
      itemKeyProp="subjectId"
      items={subjects}
      itemValueProp="subjectId"
      label="Subject"
      noSelectionValue="Select subject"
      selectedItemValue={selectedSubjectId}
      dataCyValue="subject-select"
    />
  );
};

type StateItem = {|
  stateId: string,
  stateDisplay: string,
|};

type GradeItem = {|
  gradeId: string,
  gradeDisplay: string,
|};

type SubjectItem = {|
  subjectId: string,
  subjectDisplay: string,
|};

function extractStateItems(data: State[]): StateItem[] {
  return data.map((state) => {
    return {
      stateId: state.stateId,
      stateDisplay: state.stateDisplay,
    };
  });
}

function extractGradeItems(
  data: State[],
  selectedPartnerId: ?string
): GradeItem[] {
  if (!selectedPartnerId) {
    return [];
  }
  const selectedPartner: ?State = getPartner(data, selectedPartnerId);
  if (!selectedPartner) {
    return [];
  }
  return selectedPartner.availableGrades.map((grade) => {
    return {
      gradeId: grade.gradeId,
      gradeDisplay: grade.gradeDisplay,
    };
  });
}

function extractSubjectItems(
  data: State[],
  selectedPartnerId: ?string,
  selectedGradeId: ?string
): SubjectItem[] {
  if (!selectedPartnerId || !selectedGradeId) {
    return [];
  }
  const selectedPartner: ?State = getPartner(data, selectedPartnerId);
  if (!selectedPartner) {
    return [];
  }
  const selectedGrade: ?Grade = getGrade(
    selectedPartner.availableGrades,
    selectedGradeId
  );
  if (!selectedGrade) {
    return [];
  }
  return selectedGrade.availableSubjects.map((subject) => {
    return {
      subjectId: subject.subjectId,
      subjectDisplay: subject.subjectDisplay,
    };
  });
}

export type Props = {|
  data: State[],
  onAllDataSelected: (string, string, string) => void,
  orientation: 'vertical' | 'horizontal',
  selectedGradeId?: ?string,
  selectedPartnerId?: ?string,
  selectedSubjectId?: ?string,
|};

const AldSelection = (props: Props): React.Node => {
  const [selectedPartnerId, setSelectedPartnerId]:[?string, ((?string => ?string) | ?string) => void] = React.useState(
    props.selectedPartnerId
  );
  const [selectedGradeId, setSelectedGradeId]:[?string, ((?string => ?string) | ?string) => void] = React.useState(
    props.selectedGradeId
  );
  const [selectedSubjectId, setSelectedSubjectId]:[?string, ((?string => ?string) | ?string) => void] = React.useState(
    props.selectedSubjectId
  );

  const onPartnerChange = (partnerId: ?string): void => {
    setSelectedPartnerId(partnerId);
    setSelectedGradeId(null);
    setSelectedSubjectId(null);
  };

  const onGradeChange = (gradeId: ?string): void => {
    setSelectedGradeId(gradeId);
    setSelectedSubjectId(null);
  };

  const onSubjectChange = (subjectId: ?string): void => {
    setSelectedSubjectId(subjectId);
    if (selectedPartnerId && selectedGradeId && subjectId) {
      props.onAllDataSelected(selectedPartnerId, selectedGradeId, subjectId);
    }
  };

  return (
    <form aria-label="Select options">
      <div
        className={
          props.orientation === 'vertical' ? styles.vertical : styles.horizontal
        }
      >
        <PartnerSelect
          partners={extractStateItems(props.data)}
          selectedPartnerId={selectedPartnerId}
          changeSelection={onPartnerChange}
        />
        <GradeSelect
          grades={extractGradeItems(props.data, selectedPartnerId)}
          selectedGradeId={selectedGradeId}
          changeSelection={onGradeChange}
        />
        <SubjectSelect
          subjects={extractSubjectItems(
            props.data,
            selectedPartnerId,
            selectedGradeId
          )}
          selectedSubjectId={selectedSubjectId}
          changeSelection={onSubjectChange}
        />
      </div>
    </form>
  );
};

export default AldSelection;
